/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { patternAnyEntityToPatternAnyStore } from 'src/luis/core/services/entities/mappers';
import { PatternAnyEntitySwagger } from 'src/luis/projects/entities/types/swagger/PatternAnyEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { PatternAnyEntityStore } from 'src/luis/types/stores/entities/PatternAnyEntityStore';

/**
 * Represents a service to perform CRUD operations for Pattern.Any entity.
 */
export const getPatternAnyEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param patternAnyEntityId An optional Pattern.Any entity id to add.
     * @returns A url string to call.
     */
    const getUrl = (patternAnyEntityId?: LuisId): string =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('patternAnyEntities', `${patternAnyEntityId || ''}`)
            .build();

    /**
     * Gets a Pattern.Any entity by id.
     *
     * @param id The id of the Pattern.Any entity to fetch.
     */
    const getEntity = async ({ id }: Pick<PatternAnyEntityStore, 'id'>): Promise<PatternAnyEntityStore> => {
        const { data: patternAnyEntitySwagger } = await httpService.get<PatternAnyEntitySwagger>(getUrl(id), apiOptions);

        return patternAnyEntityToPatternAnyStore(patternAnyEntitySwagger);
    };

    /**
     * Creates a new Pattern.Any entity.
     *
     * @param name The new Pattern.Any entity name.
     *
     * @returns A new Pattern.Any entity store.
     */
    const createEntity = async ({ name }: Pick<PatternAnyEntityStore, 'name'>): Promise<PatternAnyEntityStore> => {
        const { data: patternAnyEntityId } = await httpService.post<string>(getUrl(), { name }, apiOptions);

        return await getEntity({ id: patternAnyEntityId });
    };

    /**
     * Deletes the given Pattern.Any entity.
     *
     * @param id The id of the Pattern.Any entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<PatternAnyEntityStore, 'id'>): Promise<void> => {
        await httpService.delete(getUrl(id), apiOptions);
    };

    /**
     * Renames the given Pattern.Any entity.
     *
     * @param id The id of the Pattern.Any entity to rename.
     * @param name The Pattern.Any entity new name.
     */
    const renameEntity = async ({ id, name }: Pick<PatternAnyEntityStore, 'id' | 'name'>): Promise<void> => {
        await httpService.put(getUrl(id), { name, explicitList: [] }, apiOptions);
    };

    return { getEntity, createEntity, deleteEntity, renameEntity };
});
